import { Box, Button } from "@mui/joy";
import useDetectIOS from "hooks/useDetectIOS";
import { useRecoilValue } from "recoil";
import { loginHintAtom } from "recoil/loginHintAtom";
import google_logo from "static/image/common/google_logo.png";
import apple_logo from "static/image/common/apple_logo.png";
import { theme } from "styles/theme";
import kakao_logo from "../../static/image/common/kakao_logo.png";

export default function LoginButtonTemplatePromotion({
  loginType,
  action,
  isLoggedIn,
  isLoading,
  widthPx,
  heightPx,
}: {
  loginType: "apple" | "google" | "kakao";
  action: () => void;
  isLoggedIn: boolean;
  isLoading?: boolean;
  widthPx?: number | string;
  heightPx?: number | string;
}) {
  const disabled = isLoading;
  const { isIOS } = useDetectIOS();
  const loginHint = useRecoilValue(loginHintAtom);
  // 마지막 로그인 수단과 일치하는 버튼에만 "최근 로그인" 라벨 표시 (LoginButtonTemplate 과 동일)
  const isRecentLogin = loginHint === loginType && !isLoggedIn;

  const loginDataByType: {
    [key in "apple" | "google" | "kakao"]: {
      logo?: string;
      buttonText: string;
      backgroundColor: string;
      color: string;
      borderColor?: string;
    };
  } = {
    apple: {
      logo: apple_logo,
      buttonText: "Apple로 시작하기",
      backgroundColor: theme.vars.palette.Surface.Forward,
      color: theme.vars.palette.Content.Strong,
      borderColor: "none",
    },
    google: {
      logo: google_logo,
      buttonText: "Google로 시작하기",
      backgroundColor: theme.vars.palette.Surface.Forward,
      color: theme.vars.palette.Content.Strong,
      borderColor: "none",
    },
    kakao: {
      logo: kakao_logo,
      buttonText: "Kakao로 시작하기",
      backgroundColor: "#FEE500",
      color: theme.vars.palette.Content.Strong,
      borderColor: "none",
    },
  };

  const currentLoginData = loginDataByType[loginType];

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {isRecentLogin && (
        <Box
          sx={{
            position: "absolute",
            top: "-4px",
            right: "-4px",
            zIndex: 1,
            height: "20px",
            px: "6px",
            display: "flex",
            alignItems: "center",
            borderRadius: "999px",
            // Figma node 8267-19107: Content.Highlight pill + Content.Negative 글자, 버튼 우상단
            backgroundColor: theme.vars.palette.Content.Highlight,
            color: theme.vars.palette.Content.Negative,
            fontSize: "12px", // Footnote
            fontWeight: 700,
            lineHeight: "126%",
            whiteSpace: "nowrap",
            pointerEvents: "none",
            // 등장 시 툭 튀지 않도록 부드럽게 페이드 + 스케일 인 (버튼 우상단 모서리 기준)
            transformOrigin: "top right",
            animation: "recentLoginAppear 200ms ease-out",
            "@keyframes recentLoginAppear": {
              from: { opacity: 0, transform: "scale(0.8) translateY(2px)" },
              to: { opacity: 1, transform: "scale(1) translateY(0)" },
            },
          }}
        >
          최근 로그인
        </Box>
      )}
      <Button
        onClick={!disabled ? action : () => {}}
        variant={"Plain"}
        color={"neutral"}
        size="md"
        sx={{
          backgroundColor: currentLoginData.backgroundColor,
          color: currentLoginData.color,
          width: widthPx,
          "@media (min-width: 1024px)": {
            "&:hover:not(.Mui-disabled)": {
              backgroundColor: currentLoginData.backgroundColor,
              color: currentLoginData.color,
              opacity: 0.6,
            },
          },
          "@media (max-width: 1024px)": {
            "&:hover": {
              // 모바일에서 터치 끝나도 hover로 잡는 문제 때문에 hover은 1024 이상에서만 적용
              // ios : 버튼 클릭 후 touchend 해도 hover
              backgroundColor: currentLoginData.backgroundColor,
              color: currentLoginData.color,
            },
          },
          "&:focus": {
            backgroundColor: currentLoginData.backgroundColor,
            color: currentLoginData.color,
          },
          "&:active": {
            backgroundColor: currentLoginData.backgroundColor,
            color: currentLoginData.color,
          },
        }}
        startDecorator={
          <img
            src={currentLoginData.logo}
            alt={"empty"}
            style={{
              width: "20px",
              height: "20px",
              objectFit: "contain",
            }}
          />
        }
      >
        {isLoading ? "로그인 중..." : currentLoginData.buttonText}
      </Button>
    </Box>
  );
}
